import { history } from '~/constants';

const TOKEN_KEY = 'svc-onboarding-access-token';
const TOKEN_SUBJECT_KEY = 'svc-onboarding-access-token-subject';

// Which subject a stored token was minted for — an offer-flow (APPLICATION)
// token must never authenticate the standalone passcode reset (USER) route,
// or vice versa, without going through WebID identification for that subject.
export type SvcOnboardingSubjectKind = 'APPLICATION' | 'USER';

export type SvcOnboardingCallbackParams =
  | {
      outcome: 'success';
      completionCode: string;
      // Absent on the standalone passcode reset, which re-derives its
      // resetPasswordUuid from the route instead. See qred/svc-onboarding#392.
      applicationUuid?: string;
      // WebID found data that differs from ours. Not a failed identification:
      // svc-onboarding still sends a completion code, and Core raises a CRM task.
      isWebIdMismatch: boolean;
    }
  | { outcome: 'declined' }
  | { outcome: 'cancelled' };

export const svcOnboardingAuthStore = {
  setToken: (token: string, subjectKind: SvcOnboardingSubjectKind): void => {
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(TOKEN_SUBJECT_KEY, subjectKind);
  },

  getToken: (subjectKind: SvcOnboardingSubjectKind): string | null => {
    if (sessionStorage.getItem(TOKEN_SUBJECT_KEY) !== subjectKind) return null;
    return sessionStorage.getItem(TOKEN_KEY);
  },

  clearToken: (): void => {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_SUBJECT_KEY);
  },

  getCallbackParams: (): SvcOnboardingCallbackParams | null => {
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get('declinedWebIdentify') === 'true') {
      return { outcome: 'declined' };
    }
    if (params.get('cancelledWebIdentify') === 'true') {
      return { outcome: 'cancelled' };
    }
    const completionCode = params.get('complete');
    if (!completionCode) return null;
    const applicationUuid = params.get('applicationUuid') ?? undefined;
    return {
      outcome: 'success',
      completionCode,
      applicationUuid,
      isWebIdMismatch: params.get('mismatchedWebIdentify') === 'true',
    };
  },

  clearCallbackParams: (): void => {
    const url = new URL(globalThis.location.href);
    url.searchParams.delete('complete');
    url.searchParams.delete('applicationUuid');
    url.searchParams.delete('declinedWebIdentify');
    url.searchParams.delete('mismatchedWebIdentify');
    url.searchParams.delete('cancelledWebIdentify');
    history.replace({ search: url.search });
  },
};
